import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, TrendingDown, Minus, TrendingUp, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trendMeta = {
  declining: { Icon: TrendingDown, tone: 'text-red-600', label: 'Declining' },
  flat: { Icon: Minus, tone: 'text-gray-600', label: 'Flat' },
  improving: { Icon: TrendingUp, tone: 'text-emerald-600', label: 'Improving' },
  single_attempt: { Icon: Minus, tone: 'text-gray-400', label: 'New' },
};

const severityStyle = (score) => {
  if (score < 40) return { bg: 'bg-red-50 border-red-300', pill: 'bg-red-500 text-white', text: 'text-red-700', label: 'Critical' };
  if (score < 60) return { bg: 'bg-amber-50 border-amber-300', pill: 'bg-amber-500 text-white', text: 'text-amber-700', label: 'Moderate' };
  return { bg: 'bg-yellow-50 border-yellow-300', pill: 'bg-yellow-500 text-white', text: 'text-yellow-700', label: 'Review' };
};

const WeakTopicsList = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/student/weak-topics?limit=10`);
        setData(res.data);
      } catch (e) {
        console.error('weak topics load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="weak-topics-loading">
        <Loader2 className="animate-spin text-red-500" size={22} />
      </div>
    );
  }

  const topics = data?.weak_topics || [];

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="weak-topics-list">
      <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-rose-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target size={22} />
          <div>
            <h3 className="font-semibold text-lg">Focus Areas — Weak Topics</h3>
            <p className="text-red-100 text-xs">Ranked by lowest recent score — practise these first</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-white/20 px-2 py-1 rounded-full">Critical {data?.critical_count || 0}</span>
          <span className="bg-white/20 px-2 py-1 rounded-full">Moderate {data?.moderate_count || 0}</span>
          <span className="bg-white/20 px-2 py-1 rounded-full">Review {data?.review_count || 0}</span>
        </div>
      </div>
      <div className="p-4">
        {topics.length === 0 ? (
          <div className="text-center py-10">
            <AlertCircle className="mx-auto text-emerald-400 mb-3" size={40} />
            <p className="text-gray-600 font-medium">No weak topics — you&apos;re on track!</p>
            <p className="text-sm text-gray-400 mt-1">Keep attempting Topic Tests to stay ahead.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topics.map((t, i) => {
              const sev = severityStyle(t.latest_score);
              const tm = trendMeta[t.trend] || trendMeta.flat;
              const TrendIcon = tm.Icon;
              return (
                <div
                  key={`${t.subject}-${t.topic}-${i}`}
                  className={`rounded-lg border p-4 ${sev.bg}`}
                  data-testid={`weak-topic-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${sev.pill}`}>
                          {sev.label}
                        </span>
                        <span className="text-[11px] uppercase text-gray-500 tracking-wide">{t.subject}</span>
                      </div>
                      <p className="font-semibold text-gray-800 truncate" title={t.topic}>{t.topic}</p>
                      <p className="text-xs text-gray-500 mt-1">{t.recommendation}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-2xl font-bold ${sev.text}`}>{t.latest_score}%</p>
                      <div className={`flex items-center justify-end gap-1 text-xs ${tm.tone}`}>
                        <TrendIcon size={12} />
                        <span>{tm.label}{t.delta !== 0 ? ` ${t.delta > 0 ? '+' : ''}${t.delta}%` : ''}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{t.attempts} attempt{t.attempts !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeakTopicsList;
